import * as React from 'react';
import Modal from 'react-modal';
import {useVideoFrameSelector} from './video-frame-selector.use-case';
import type {VideoFrameSelectorProps} from './video-frame-selector.type';

// Configurar o app element para o react-modal (para acessibilidade)
if (typeof window !== 'undefined') {
  Modal.setAppElement(document.body);
}

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 9999,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    padding: '20px',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
};

export const VideoFrameSelector: React.FC<VideoFrameSelectorProps> = ({
  videoUrl,
  onFrameSelected,
  onClose,
}) => {
  const _useUseCase = useVideoFrameSelector({
    videoUrl,
    onFrameSelected,
  });

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(event.target.value);
    _useUseCase.handleTimeChange(time);
  };

  return (
    <Modal
      isOpen={true}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel="Selecionar Cover do Vídeo"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}>
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}>
          <h4 style={{margin: 0}}>Selecionar Cover do Vídeo</h4>
          <button
            type="button"
            className="btn-flat"
            onClick={onClose}
            style={{padding: '5px 10px'}}>
            <i className="material-icons">close</i>
          </button>
        </div>

        {_useUseCase.error && (
          <div
            className="card-panel red lighten-4 red-text text-darken-2"
            style={{marginBottom: '20px'}}>
            {_useUseCase.error}
          </div>
        )}

        <div style={{marginBottom: '20px'}}>
          <video
            ref={_useUseCase.videoRef}
            src={videoUrl}
            style={{width: '100%', maxHeight: '300px', display: 'none'}}
            preload="metadata"
            muted
            playsInline
            onLoadStart={() =>
              console.log('🎬 Iniciando carregamento do vídeo')
            }
            onLoadedMetadata={() => console.log('🎬 Metadata carregada')}
            onLoadedData={() => console.log('🎬 Dados carregados')}
            onCanPlay={() => console.log('🎬 Pode reproduzir')}
            onError={(e) => console.error('🚫 Erro no vídeo:', e)}
          />

          <div style={{position: 'relative'}}>
            <canvas
              ref={_useUseCase.canvasRef}
              style={{
                width: '100%',
                maxHeight: '300px',
                minHeight: '200px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                objectFit: 'contain',
                backgroundColor: '#f5f5f5',
                display: 'block',
              }}
              width="800"
              height="450"
            />

            {_useUseCase.isLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '4px',
                }}>
                Carregando frame...
              </div>
            )}
          </div>
        </div>

        <div style={{marginBottom: '20px'}}>
          <label
            htmlFor="time-slider"
            style={{display: 'block', marginBottom: '10px'}}>
            Selecione o momento do vídeo:{' '}
            <strong>
              {_useUseCase.formatTime(_useUseCase.currentTime)} /{' '}
              {_useUseCase.formatTime(_useUseCase.duration)}
            </strong>
          </label>
          <input
            id="time-slider"
            type="range"
            min={0}
            max={Math.max(_useUseCase.duration, 60)} // Usar 60s como máximo se duration desconhecido
            step={0.5}
            value={_useUseCase.currentTime}
            onChange={handleTimeChange}
            style={{width: '100%'}}
            disabled={_useUseCase.isLoading}
          />

          {_useUseCase.duration <= 60 && _useUseCase.duration > 0 && (
            <div style={{color: 'blue', fontSize: '12px', marginTop: '5px'}}>
              ℹ️ Duração estimada:{' '}
              {_useUseCase.formatTime(_useUseCase.duration)} - Navegue pelo
              slider para selecionar frames
            </div>
          )}

          {_useUseCase.duration === 60 && (
            <div style={{color: 'orange', fontSize: '12px', marginTop: '5px'}}>
              ⚠️ Duração do vídeo desconhecida - Use o slider para navegar (máx
              60s) ou clique "Capturar"
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '10px',
          }}>
          <div style={{display: 'flex', gap: '5px'}}>
            <button
              type="button"
              className="btn-small orange lighten-1"
              onClick={() => {
                console.log('🔧 Tentativa manual de captura...');
                _useUseCase.handleTimeChange(_useUseCase.currentTime);
              }}>
              Capturar
            </button>
          </div>
          <div style={{display: 'flex', gap: '10px'}}>
            <button
              type="button"
              className="btn-flat"
              onClick={onClose}
              disabled={_useUseCase.isLoading}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn waves-effect waves-light"
              onClick={_useUseCase.handleSelectFrame}
              disabled={!_useUseCase.previewFrameUrl || _useUseCase.isLoading}>
              {_useUseCase.isLoading ? 'Processando...' : 'Usar este Frame'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
